import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  adminLogin,
  adminLogout,
  adminMe,
  getAccessToken,
  setOnUnauthorized,
} from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null until checked, false if anon
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Global handler: when a 401 leaks past axios refresh attempts, log out + redirect.
  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(false);
      if (location.pathname.startsWith("/admin") && location.pathname !== "/admin/login") {
        navigate("/admin/login", { replace: true });
      }
    });
  }, [navigate, location.pathname]);

  // Only check session if we have a token (avoid 401 noise on public pages).
  useEffect(() => {
    let mounted = true;
    const token = getAccessToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    adminMe()
      .then((u) => mounted && setUser(u))
      .catch(() => mounted && setUser(false))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const u = await adminLogin(email, password);
    setUser(u);
    return u;
  };

  const logout = async () => {
    await adminLogout();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
